import React from "react";

import LessonSchedule from "./LessonSchedule";
import ScheduleBlock from "./ScheduleBlock";
import MonthEvent from "./MonthEvent";
import ScheduleEvent from "./ScheduleEvent";

import styles from "./styles.module.scss";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import Program from "./Program";
import TimeSlotReservation from "./TimeSlotReservation";
import { cardLabels } from "./cardLabels";

type PropsDef = {
  event: any;
  timeText: string;
  timeZone?: string;
  viewType?: string;
  // True while the facility calendar draws the bay columns.
  facilityResourceView?: boolean;
};

function getMinutesDifferenceFromTimeText(timeText: string): number | null {
  const [startTime, endTime] = timeText.split(" - ");

  if (!startTime || !endTime) {
    return null;
  }

  let start = DateTime.fromFormat(startTime.trim(), "h:mm a");
  let end = DateTime.fromFormat(endTime.trim(), "h:mm a");

  if (!start.isValid) {
    start = DateTime.fromFormat(startTime.trim(), "h a");
  }
  if (!end.isValid) {
    end = DateTime.fromFormat(endTime.trim(), "h a");
  }

  if (!start.isValid || !end.isValid) {
    return null;
  }

  const diffInMinutes = end.diff(start, "minutes").minutes;

  return diffInMinutes;
}

function formatTimeRange(start: Date, end: Date, timeZone: string): string {
  const startTime = DateTime.fromJSDate(start, { zone: timeZone }).toFormat(
    "h:mm a",
  );
  const endTime = DateTime.fromJSDate(end, { zone: timeZone }).toFormat(
    "h:mm a",
  );

  return `${startTime} - ${endTime}`;
}

export default function Event(props: PropsDef) {
  const { t } = useTranslation();

  if (props.viewType === "dayGridMonth") {
    return <MonthEvent {...props} />;
  }

  if (props.viewType === "listWeek") {
    return <ScheduleEvent {...props} />;
  }

  const labels = cardLabels(
    props.event.extendedProps,
    props.facilityResourceView === true,
  );

  if (props.event.extendedProps.type === "lesson_schedule") {
    return (
      <LessonSchedule
        {...props}
        resourceLabel={labels.resourceLabel}
        coachName={labels.coachName}
      />
    );
  } else if (props.event.extendedProps.type === "coach_schedule_block") {
    return <ScheduleBlock {...props} />;
  } else if (props.event.extendedProps.type === "program") {
    return <Program {...props} />;
  } else if (props.event.extendedProps.type === "time_slot_reservation") {
    return (
      <TimeSlotReservation
        {...props}
        resourceLabel={labels.resourceLabel}
        coachName={labels.coachName}
      />
    );
  } else if (!props.event.extendedProps?.student_profiles) {
    return (
      <div className={styles.zero_students}>
        <div className={styles.titleEvent}>{t("0 Students")}</div>
      </div>
    );
  } else {
    const timeDifference = getMinutesDifferenceFromTimeText(props.timeText);
    const timeRange = formatTimeRange(
      props.event.start,
      props.event.end,
      props.timeZone,
    );

    return (
      <div className={styles.eventWithoutTitle}>
        <div className={styles.titleEvent}>{t("(No Title)")}</div>
        {timeDifference && timeDifference > 45 && (
          <div className={styles.timeEvent}>{timeRange}</div>
        )}
      </div>
    );
  }
}
